import React, { useState, useEffect, useCallback } from 'react';
import { 
  Server, 
  RefreshCw, 
  Wifi, 
  WifiOff, 
  Clock,
  Settings,
  AlertTriangle,
  CheckCircle,
  Activity,
  Zap,
  X,
  Loader2
} from 'lucide-react';
import { NativeButton, NativeSelect } from '../components/NativeComponents';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');

const deviceService = {
  list: async () => {
    const response = await fetch(`${API_URL}/api/devices`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    if (!response.ok) throw new Error('API error');
    return response.json();
  },
  getDetail: async (chargerId) => {
    const response = await fetch(`${API_URL}/api/devices/${chargerId}`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    if (!response.ok) throw new Error('API error');
    return response.json();
  },
  updateVersion: async (chargerId, version) => {
    const response = await fetch(`${API_URL}/api/devices/${chargerId}/version`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ version })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Güncelleme başarısız');
    return data;
  }
};

export default function DeviceManagerPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deviceDetail, setDeviceDetail] = useState(null);
  const [updatingVersion, setUpdatingVersion] = useState(null);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const result = await deviceService.list();
      setDevices(result.devices || []);
    } catch (err) {
      toast.error('Cihazlar yüklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleShowDetail = async (device) => {
    setSelectedDevice(device);
    setDetailLoading(true);
    try {
      const result = await deviceService.getDetail(device.charger_id);
      setDeviceDetail(result);
    } catch (err) {
      toast.error('Detaylar yüklenemedi');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleVersionChange = async (chargerId, newVersion) => {
    setUpdatingVersion(chargerId);
    try {
      await deviceService.updateVersion(chargerId, newVersion);
      toast.success(`OCPP versiyonu ${newVersion} olarak güncellendi`);
      fetchData();
      if (deviceDetail && deviceDetail.device?.charger_id === chargerId) {
        handleShowDetail({ charger_id: chargerId });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingVersion(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const getVersionBadge = (version) => {
    if (!version) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
          <AlertTriangle className="w-3 h-3" />
          Atanmadı
        </span>
      );
    }
    const colors = version === "2.0.1" 
      ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
      : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${colors}`}>
        OCPP {version}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="device-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cihaz Yöneticisi</h1>
          <p className="text-muted-foreground">Bağlı OCPP cihazlarını yönetin</p>
        </div>
        <NativeButton 
          variant="outline" 
          onClick={() => fetchData(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Yenile
        </NativeButton>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Server className="w-8 h-8 text-primary" />
            <div>
              <p className="text-2xl font-bold text-foreground">{devices.length}</p>
              <p className="text-sm text-muted-foreground">Toplam Cihaz</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Wifi className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-foreground">
                {devices.filter(d => d.is_online).length}
              </p>
              <p className="text-sm text-muted-foreground">Çevrimiçi</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-bold text-sm">1.6</div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {devices.filter(d => d.ocpp_version === "1.6").length}
              </p>
              <p className="text-sm text-muted-foreground">OCPP 1.6</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 font-bold text-xs">2.0</div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {devices.filter(d => d.ocpp_version === "2.0.1").length}
              </p>
              <p className="text-sm text-muted-foreground">OCPP 2.0.1</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cihaz Listesi */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Server className="w-5 h-5" />
            Cihaz Listesi
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Cihaz ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Durum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">OCPP Versiyon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Üretici / Model</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Son Görülme</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Oturumlar</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {devices.map((device) => (
                <tr key={device.charger_id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-mono font-medium text-foreground">{device.charger_id}</p>
                      {device.station_name && (
                        <p className="text-xs text-muted-foreground">{device.station_name}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {device.is_online ? (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <Wifi className="w-4 h-4" />
                        <span className="text-sm">Çevrimiçi</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-500">
                        <WifiOff className="w-4 h-4" />
                        <span className="text-sm">Çevrimdışı</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getVersionBadge(device.ocpp_version)}
                      <NativeSelect
                        value={device.ocpp_version || ""}
                        onChange={(e) => handleVersionChange(device.charger_id, e.target.value)}
                        disabled={updatingVersion === device.charger_id}
                        className="w-24 text-xs"
                      >
                        <option value="">Seç</option>
                        <option value="1.6">1.6</option>
                        <option value="2.0.1">2.0.1</option>
                      </NativeSelect>
                      {updatingVersion === device.charger_id && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-foreground">{device.vendor || '-'}</p>
                    <p className="text-xs text-muted-foreground">{device.model || '-'}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {formatDateTime(device.last_seen)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground">{device.session_count || 0}</span>
                  </td>
                  <td className="px-6 py-4">
                    <NativeButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleShowDetail(device)}
                    >
                      <Settings className="w-4 h-4" />
                    </NativeButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detay Modal */}
      {selectedDevice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-card">
              <h3 className="text-lg font-semibold text-foreground">
                Cihaz Detayları: {selectedDevice.charger_id}
              </h3>
              <NativeButton variant="ghost" size="sm" onClick={() => setSelectedDevice(null)}>
                <X className="w-4 h-4" />
              </NativeButton>
            </div>
            
            {detailLoading ? (
              <div className="flex items-center justify-center p-8">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : deviceDetail ? (
              <div className="p-6 space-y-6">
                {/* Cihaz Bilgileri */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Cihaz ID</p>
                    <p className="font-mono font-medium text-foreground">{deviceDetail.device?.charger_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">OCPP Versiyonu</p>
                    <p className="font-medium text-foreground">{getVersionBadge(deviceDetail.device?.ocpp_version)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Üretici</p>
                    <p className="text-foreground">{deviceDetail.device?.vendor || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Model</p>
                    <p className="text-foreground">{deviceDetail.device?.model || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Seri No</p>
                    <p className="font-mono text-foreground">{deviceDetail.device?.serial_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Firmware</p>
                    <p className="text-foreground">{deviceDetail.device?.firmware_version || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Son Heartbeat</p>
                    <p className="text-foreground">{formatDateTime(deviceDetail.device?.last_seen)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Son Boot</p>
                    <p className="text-foreground">{formatDateTime(deviceDetail.device?.last_boot)}</p>
                  </div>
                </div>

                {/* İstatistikler */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Activity className="w-6 h-6 text-primary" />
                      <div>
                        <p className="text-2xl font-bold text-foreground">{deviceDetail.stats?.total_sessions || 0}</p>
                        <p className="text-sm text-muted-foreground">Toplam Oturum</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Zap className="w-6 h-6 text-yellow-500" />
                      <div>
                        <p className="text-2xl font-bold text-foreground">
                          {((deviceDetail.stats?.total_energy || 0) / 1000).toFixed(1)} kWh
                        </p>
                        <p className="text-sm text-muted-foreground">Toplam Enerji</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Son Oturumlar */}
                {deviceDetail.recent_sessions?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Son Oturumlar</h4>
                    <div className="space-y-2">
                      {deviceDetail.recent_sessions.slice(0, 5).map((session, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">{formatDateTime(session.start_time)}</span>
                          <span className="text-foreground">{((session.energy_consumed || 0) / 1000).toFixed(2)} kWh</span>
                          <span className="text-foreground">{(session.total_cost || 0).toFixed(2)} ₺</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
